import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, AlertTriangle, Crown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getUserUsageStatsWithDefaults, UserUsageStats } from '@/services/ai-message-limits-service';
import { useAuth } from '@/contexts/AuthContext';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnimatedCounter } from '@/hooks/use-animated-counter';
import { Skeleton } from '@/components/ui/skeleton';

interface AiMessageUsageCardProps {
  className?: string;
  enableRealTimeUpdates?: boolean;
}

const topicLabels = {
  guide: 'Guide AI',
  performance: 'Performance AI', 
  trading_strategy: 'Trading Strategy AI',
  advisor: 'Advisor AI'
};

const UsageItemSkeleton = React.memo(() => (
  <div className="border rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-5 w-12" />
    </div>
    <div className="space-y-3">
      <div>
        <div className="flex justify-between text-xs mb-1">
          <Skeleton className="h-3 w-10" />
          <Skeleton className="h-3 w-8" />
        </div>
        <Skeleton className="h-2 w-full" />
      </div>
      <div>
        <div className="flex justify-between text-xs mb-1">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-8" />
        </div>
        <Skeleton className="h-2 w-full" />
      </div>
    </div>
  </div>
));

UsageItemSkeleton.displayName = 'UsageItemSkeleton';

  const UsageItem = React.memo<{ 
    stat: UserUsageStats; 
    isMobile: boolean; 
    isUpdating: boolean; 
  }>(({ stat, isMobile, isUpdating }) => {
    // Animated counters for this specific stat
    const dailyUsageAnimated = useAnimatedCounter({ 
      target: stat.daily_usage,
      duration: 350
    });
    const monthlyUsageAnimated = useAnimatedCounter({ 
      target: stat.monthly_usage,
      duration: 350,
      delay: 50
    });

    const dailyPercentage = Math.round((stat.daily_usage / stat.daily_limit) * 100);
    const monthlyPercentage = Math.round((stat.monthly_usage / stat.monthly_limit) * 100);

    return (
      <motion.div
        className="border rounded-lg p-4 bg-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-sm">{topicLabels[stat.topic as keyof typeof topicLabels]}</span>
          <Badge variant="outline" className="text-xs">
            {stat.package_name}
          </Badge>
        </div>
        
        <div className="space-y-3">
          {/* Daily Usage */}
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Daily</span>
              <span>{dailyUsageAnimated.current}/{stat.daily_limit}</span>
            </div>
            <Progress value={dailyPercentage} className="h-2" />
          </div>
          
          {/* Monthly Usage */}
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Monthly</span>
              <span>{monthlyUsageAnimated.current}/{stat.monthly_limit}</span>
            </div>
            <Progress value={monthlyPercentage} className="h-2" />
          </div>
        </div>
      </motion.div>
    );
  });



UsageItem.displayName = 'UsageItem';

export const AiMessageUsageCard: React.FC<AiMessageUsageCardProps> = ({
  className = '',
  enableRealTimeUpdates = true
}) => {
  const { user } = useAuth();
  const [usageStats, setUsageStats] = useState<UserUsageStats[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const isMobile = useIsMobile();

  // Try to get AI assistant context if available, but don't fail if it's not
  let usageUpdateTrigger = 0;
  try {
    const aiContext = useAiAssistant();
    usageUpdateTrigger = aiContext?.usageUpdateTrigger || 0;
  } catch (error) {
    // Context not available (e.g., in settings page), use default value
  }

  // Immediate fetch function for real-time updates
  const fetchUsageStats = useCallback(async (isInitial = false) => {
    if (!user?.id) return;

    try {
      // Minimal loading indication for subsequent updates
      if (!isInitial) {
        setIsUpdating(true);
      }
      
      const stats = await getUserUsageStatsWithDefaults(user.id);
      setUsageStats(stats);
      
      // Hide loading indicator immediately
      if (!isInitial) {
        setIsUpdating(false);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      // Set default stats to show limits even if there's an error
      setUsageStats([
        {
          topic: 'guide',
          daily_usage: 0,
          monthly_usage: 0,
          daily_limit: 5,
          monthly_limit: 100,
          package_name: 'free'
        },
        {
          topic: 'performance',
          daily_usage: 0,
          monthly_usage: 0,
          daily_limit: 5,
          monthly_limit: 100,
          package_name: 'free'
        },
        {
          topic: 'trading_strategy',
          daily_usage: 0,
          monthly_usage: 0,
          daily_limit: 5,
          monthly_limit: 100,
          package_name: 'free'
        },
        {
          topic: 'advisor',
          daily_usage: 0,
          monthly_usage: 0,
          daily_limit: 5,
          monthly_limit: 100,
          package_name: 'free'
        }
      ]);
      
      if (!isInitial) {
        setIsUpdating(false);
      }
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  }, [user?.id]);

  // Initial fetch on mount
  useEffect(() => {
    fetchUsageStats(true);
  }, [fetchUsageStats]);

  // Re-fetch immediately when usage is updated (real-time updates) - only if enabled
  useEffect(() => {
    if (enableRealTimeUpdates && usageUpdateTrigger > 0 && !initialLoading) {
      // Immediate fetch for real-time updates
      fetchUsageStats(false);
    }
  }, [usageUpdateTrigger, fetchUsageStats, initialLoading, enableRealTimeUpdates]);

  // Check for any warnings
  const hasWarnings = usageStats.some(stat => {
    const dailyPercentage = (stat.daily_usage / stat.daily_limit) * 100;
    const monthlyPercentage = (stat.monthly_usage / stat.monthly_limit) * 100;
    return dailyPercentage >= 80 || monthlyPercentage >= 80;
  });

  const hasExceeded = usageStats.some(stat => {
    const dailyPercentage = (stat.daily_usage / stat.daily_limit) * 100;
    const monthlyPercentage = (stat.monthly_usage / stat.monthly_limit) * 100;
    return dailyPercentage >= 100 || monthlyPercentage >= 100;
  });

  return (
    <Card className={className}>
      <CardHeader className={isMobile ? "p-4" : "p-6"}>
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 md:h-5 md:w-5 text-primary" />
          <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>AI Message Usage</CardTitle>
          <AnimatePresence>
            {isUpdating && !initialLoading && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.1 }}
                className="h-3 w-3 rounded-full bg-primary ml-auto"
              />
            )}
          </AnimatePresence>
        </div>
      </CardHeader>
      <CardContent className={isMobile ? "p-4 pt-0" : "p-6 pt-0"}>
        {initialLoading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, index) => (
              <UsageItemSkeleton key={index} />
            ))}
          </div>
        ) : (
          <>
            {/* Warnings */}
            <AnimatePresence>
              {hasExceeded && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert className="mb-4 border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      You've reached message limits for some AI assistants.
                      {usageStats[0]?.package_name === 'free' && (
                        <span className="block mt-1">
                          <Crown className="h-3 w-3 inline mr-1" />
                          Upgrade to a paid plan for higher limits.
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {hasWarnings && !hasExceeded && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert className="mb-4 border-yellow-200 bg-yellow-50">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      You're approaching message limits for some AI assistants.
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div 
              className="space-y-4"
              layout
              transition={{ duration: 0.3 }}
            >
              {usageStats.map((stat) => (
                <UsageItem
                  key={stat.topic}
                  stat={stat}
                  isMobile={isMobile}
                  isUpdating={isUpdating}
                />
              ))}
            </motion.div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
